import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { DashboardApi } from '@shared/api/rest';
import type { Notification, UpdateNotificationRequest } from '@shared/api/rest/dashboards';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getNotificationsEffect = createEffect((params: IQueryParams) => DashboardApi.getNotifications(params?.signal));
const updateNotificationsEffect = createEffect((params: IQueryParams<UpdateNotificationRequest>) =>
  DashboardApi.updateNotifications(params.request, params?.signal),
);
const deleteNotificationsEffect = createEffect((params: IQueryParams<UpdateNotificationRequest>) =>
  DashboardApi.deleteNotification(params.request, params?.signal),
);
const clearEvent = createEvent();

const $dataStore = createStore<Notification[]>([])
  .on(getNotificationsEffect.done, (_, data) => data.result.data ?? [])
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getNotificationsEffect.pending, (_, data) => data)
  .on(getNotificationsEffect.fail, () => false)
  .on(getNotificationsEffect.done, () => false)
  .on(deleteNotificationsEffect.pending, (_, data) => data)
  .on(deleteNotificationsEffect.fail, () => false)
  .on(deleteNotificationsEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getNotificationsEffect, () => null)
  .on(getNotificationsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getNotificationsEffect.done, () => null)
  .on(deleteNotificationsEffect, () => null)
  .on(deleteNotificationsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteNotificationsEffect.done, () => null)
  .on(updateNotificationsEffect, () => null)
  .on(updateNotificationsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateNotificationsEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

sample({
  source: deleteNotificationsEffect.doneData,
  filter: (data) => data.data != null,
  target: clearEvent,
});
updateNotificationsEffect.done.watch(() => getNotificationsEffect({ signal: undefined, request: undefined }));

export const $dashboardNotificationStore = {
  events: {
    getAll: ApiUtils.wrapperApi(getNotificationsEffect, RequestStrategyEnum.Debounce),
    markAllAsRead: ApiUtils.wrapperApi(updateNotificationsEffect, RequestStrategyEnum.Debounce),
    delete: ApiUtils.wrapperApi(deleteNotificationsEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
